import type { Instance } from "@popperjs/core";

/**
 * Props for the Popper component.
 */
export interface PopperProps {
  /**
   * Position of the popup
   * @default "bottom"
   */
  position?:
    | "top-start"
    | "top"
    | "top-end"
    | "right-start"
    | "right"
    | "right-end"
    | "bottom-start"
    | "bottom"
    | "bottom-end"
    | "left-start"
    | "left"
    | "left-end";
  /**
   * The wrapper around the children property is, by default, inline-block.
   * If your component has different rendering needs pass in the `display: x`
   * attribute value here.
   * @default "inline-block"
   */
  targetDisplay?: string;
  /**
   * Whether to render the popover or not
   * @default false
   */
  isOpen?: boolean;
  /**
   * The popup element
   */
  popupElement: React.ReactNode;
  /**
   * Close the element when a use taps or clicks outside of it
   * @default false
   */
  closeOnClickAway?: boolean;
  /**
   * Ignores close on click away used for specific scenarios
   * @default false
   */
  ignoreCloseOnClickAway?: boolean;
  /**
   * When using closeOnClickAway, this function is called when the user clicks
   * away.
   */
  onSetOpen?: ((open: boolean) => void) | null;
  /**
   * Arrow that points toward the reference element.
   * @default false
   */
  displayArrow?: boolean;
  /**
   * Animation that will be used every time the popover is displayed
   */
  animate?: "height" | "visibility";
  /**
   * Class that will be applied to the arrow that points toward the reference
   * element
   */
  arrowClassName?: string;
  /**
   * Whether the popup renders in a portal or inline with the rest of the DOM
   * @default true
   */
  renderInPortal?: boolean;
  /**
   * If true, popper will have same width as the target
   * @default false
   */
  sameWidth?: boolean;
  /**
   * Additional class names to pass to the Popper component
   */
  className?: string;
}

export type CreatePopperInstanceFactory = (
  sameWidth: boolean,
  displayArrow: boolean
) => (
  popperInstance: React.MutableRefObject<Instance | null>,
  targetElementRef: React.RefObject<HTMLDivElement>,
  popperElementRef: React.RefObject<HTMLDivElement>,
  position: PopperProps["position"]
) => void;

export type DestroyPopperInstanceFactory = () => (
  popperInstance: React.MutableRefObject<Instance | null>
) => void;
